import StarrySkyBackground from '@/components/StarrySkyBackground';
import StripeCheckoutSheet from '@/components/StripeEmbeddedCheckout';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getEnvConfig } from '@/config/env.config';
import { useUser } from "@/services/auth-service";
import { motion } from "framer-motion";
import { Check, Crown, Sparkles, Wand2, X } from "lucide-react";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const config = getEnvConfig();

const STRIPE_YEARLY_PRICE_IDS = {
  'Family': config.FAMILY_YEARLY_PLAN,
  'Family Pro': config.FAMILY_YEARLY_PRO_PLAN
};

const STRIPE_MONTHLY_PRICE_IDS = {
  'Family': config.FAMILY_MONTHLY_PLAN,
  'Family Pro': config.FAMILY_MONTHLY_PRO_PLAN
}

const PricingCard = ({ plan, price, description, features, downsides, icon: Icon, popular = false, trial = null, isYearly }) => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const navigate = useNavigate();
  const user = useUser();

  const handleCheckout = (withTrial = false) => {
    if (price === 0 && !user?.email) {
      navigate('/signup');
      return;
    }

    if (!user?.email) {
      // Redirect to signup with return URL please
      navigate('/signup?return_to=/plans');
      return;
    }

    if (price === 0 && user?.email) {
      navigate('/create');
      return;
    }
    setIsTrial(withTrial);
    setIsCheckoutOpen(true);
  };

  return(
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
      className="w-full h-full"
    >
      <div className="relative h-full">
        <Card className={`relative h-full pb-16 flex flex-col ${popular ? 'border-primary shadow-lg' : ''}`}>
          {popular && (
            <div className="absolute left-0 right-0 flex justify-center -top-3">
              <Badge variant="default" className="bg-primary text-slate-950">
                Most Popular
              </Badge>
            </div>
          )}
          <CardHeader className={`text-center ${popular ? 'pt-8' : 'pt-6'}`}>
            <div className="flex items-center justify-center w-12 h-12 p-2 mx-auto mb-4 rounded-full bg-primary/10">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">{plan}</CardTitle>
            <div className="flex items-baseline justify-center gap-1 mt-4">
              <span className="text-3xl font-bold">${price}</span>
              {price > 0 && <span className="text-muted-foreground">/{isYearly ? ' year' : ' month'}</span>}
            </div>
            <CardDescription className="mt-4">{description}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            {trial && !user?.has_used_trial && price > 0 && (
              <div className="mb-6 text-center">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCheckout(true)}
                >
                  Start {trial}-day free trial
                </Button>
              </div>
            )}
            <ul className="flex-1 space-y-4">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
              {downsides.map((downside, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>{downside}</span>
                  </li>
              ))}
            </ul>
          </CardContent>

          <div className="absolute bottom-4 left-6 right-6">
            <Button
                className="w-full h-12 text-lg shadow-lg"
                variant={popular ? "default" : "outline"}
                size="lg"
                onClick={() => handleCheckout(false)}
              >
                {price === 0 ? "Get Started" : "Subscribe Now"}
              </Button>
          </div>
        </Card>
      </div>

      <StripeCheckoutSheet
          isOpen={isCheckoutOpen}
          onClose={() => {
            setIsCheckoutOpen(false);
            setIsTrial(false);
          }}
          priceId={isYearly ? STRIPE_YEARLY_PRICE_IDS[plan] : STRIPE_MONTHLY_PRICE_IDS[plan]}
          isTrial={isTrial}
      />
    </motion.div>
  );
};

const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(false);

  const basePlans = [
    {
      plan: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: "Perfect for trying out WhimsyQuill",
      icon: Sparkles,
      features: [
        "Create a single story",
        "Standard art styles",
        "Save stories to your library",
        "Read on any device",
      ],
      downsides: [
        "Only one custom character per story",
        "Lowest priority story generation",
        "Only default art style",
        "Fewer images"
      ],
    },
    {
      plan: "Family",
      monthlyPrice: 9.99,
      yearlyPrice: 99.99,
      description: "Everything you need for magical storytelling",
      icon: Wand2,
      popular: true,
      trial: 30,
      features: [
        "Create 5 stories per day",
        "Up to 3 custom characters per story",
        "Save up to 10 story characters",
        "Premium story templates",
        "Priority story generation",
      ],
      downsides: [
        "Limited art styles",
        "Fewer Images"
      ],
    },
    {
      plan: "Family Pro",
      monthlyPrice: 14.99,
      yearlyPrice: 149.99,
      description: "Perfect for families with multiple children",
      icon: Crown,
      trial: 14,
      features: [
        "Create 10 stories per day",
        "Up to 5 custom characters per story",
        "Unlimited saved story characters",
        "Premium story templates",
        "Highest priority story generation",
        "Create longer stories",
        "All art styles",
        "More Images"
      ],
      downsides: [],
    },
  ];

  const plans = basePlans.map(plan => ({
    ...plan,
    price: isYearly ? plan.yearlyPrice : plan.monthlyPrice
  }));

  return (
    <div className="pb-16 min-h-fit">
      <StarrySkyBackground showAurora={true} showClouds={false} showShootingStar={false} />
      <div className="container px-4 pt-16 mx-auto">
        <div className="mb-16 text-center">
          <h1 className="mb-4 text-4xl font-bold">Choose Your Story Plan</h1>
          <p className="max-w-2xl mx-auto mb-8 text-xl text-muted-foreground">
            Start creating magical stories today!
            Unlock the power of imagination with our flexible pricing plans.
          </p>

          <div className="flex items-center justify-center my-8">
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="relative h-16 overflow-hidden rounded-full cursor-pointer w-80 bg-sky-50 dark:bg-secondary"
              role="switch"
              aria-checked={isYearly}
            >
              <motion.div
                className="absolute top-1 bottom-1 w-[49%] bg-primary rounded-full"
                animate={{ x: isYearly ? 'calc(100% + 2px)' : '4px' }}
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
              <div className="relative flex items-center justify-between h-full text-lg font-medium">
                <span className={`flex-1 text-center transition-colors duration-200 ${!isYearly ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                  Monthly
                </span>
                <span className={`flex-1 text-center transition-colors duration-200 ${isYearly ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                  Yearly
                  <span className="block text-sm opacity-90">(Save 15%)</span>
                </span>
              </div>
            </button>
          </div>
        </div>

        <div className="grid h-full grid-cols-1 gap-8 mx-auto md:grid-cols-2 lg:grid-cols-3 max-w-7xl">
          {plans.map((plan) => (
            <PricingCard
              key={plan.plan}
              {...plan}
              isYearly={isYearly}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <h2 className="mb-4 text-2xl font-bold">Need Something Special?</h2>
          <p className="mb-6 text-muted-foreground">
            Looking for custom solutions for larger groups?
          </p>
          <Button variant="outline" size="lg">
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
